import PropTypes from "prop-types";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Logo from "../../components/header/Logo";
import NavMenu from "../../components/header/NavMenu";
import IconGroup from "../../components/header/IconGroup";
import MobileNavDrawer from "../../components/header/MobileNavDrawer";
import UspBar from "../../components/header/UspBar";
import styled from "styled-components";
import { device } from "../../utils/breakpoints";
import { Link } from "react-router-dom";
import urls from "../../urls";
import { colors } from "../../utils/theme";
import { useSelector } from "react-redux";
import _ from "lodash";
import discountBannerData from "../../data/banner/banner-discount.json";
import DiscountBanner from "../../components/banner/DiscountBanner";
import MobileAccountDrawer from "../../components/header/MobileAccountDrawer";
import { useAuthContext } from "../../context/AuthContext";
import SiteWideSearch from "../../components/header/sub-components/SiteWideSearch";
import { useOverlay } from "../../context/OverlayContext";

// Fallback sticky header height on mobile to avoid initial overlap
const DEFAULT_STICKY_HEIGHT_MOBILE = 76;

const Header = ({
  layout,
  top,
  borderStyle,
  headerPaddingClass,
  headerPositionClass,
  headerBgClass,
  discountBannerActive,
}) => {
  const { authUser } = useAuthContext();
  const cartData = useSelector((state) => state.cartData);
  const [scroll, setScroll] = useState(0);
  const [headerTop, setHeaderTop] = useState(0);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const stickyRef = useRef(null);
  const [stickyHeight, setStickyHeight] = useState(0);
  const [isMeasured, setIsMeasured] = useState(false);
  let cartItemsAmount = _.sumBy(cartData, "quantity");
  const { showOverlay } = useOverlay();

  useEffect(() => {
    const header = document.querySelector(".sticky-bar");
    setHeaderTop(header.offsetTop);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (stickyRef.current) {
        setStickyHeight(stickyRef.current.clientHeight || 0);
      }
      if (!isMeasured) setIsMeasured(true);
    };
    // Synchronously measure, then measure again on next frame to catch late style/layout
    updateHeight();
    const raf1 = window.requestAnimationFrame(() => {
      updateHeight();
      const raf2 = window.requestAnimationFrame(() => updateHeight());
      // store raf2 id on raf1 via closure
      updateHeight._raf2 = raf2;
    });
    window.addEventListener("resize", updateHeight);
    // Observe dynamic size changes of sticky header
    let ro;
    if (stickyRef.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => updateHeight());
      ro.observe(stickyRef.current);
    }
    return () => {
      window.removeEventListener("resize", updateHeight);
      if (ro) ro.disconnect();
      window.cancelAnimationFrame(raf1);
      if (updateHeight._raf2) window.cancelAnimationFrame(updateHeight._raf2);
    };
  }, [isMeasured]);

  // Ensure measurement after all resources load (fonts, images) to avoid mismatch
  useEffect(() => {
    const onLoad = () => {
      if (stickyRef.current) {
        setStickyHeight(stickyRef.current.clientHeight || 0);
        setIsMeasured(true);
      }
    };
    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);

  const handleScroll = () => {
    setScroll(window.scrollY);
  };

  const closeMenu = () => {
    setNavMenuOpen(false);
  };

  return (
    <header
      style={{ position: showOverlay ? "static" : "relative", zIndex: 99 }}
    >
      <DesktopHeader
        className={`header-area clearfix ${
          headerBgClass ? headerBgClass : ""
        } ${headerPositionClass ? headerPositionClass : ""}`}
      >
        <div>
          <UspBar borderStyle={borderStyle} />
          {discountBannerActive && <DiscountBanner data={discountBannerData} />}
        </div>

        <DesktopHeaderContent
          className={` ${
            headerPaddingClass ? headerPaddingClass : ""
          } sticky-bar header-res-padding clearfix bg-shadow ${
            scroll > headerTop ? "stick" : ""
          }`}
        >
          <div className={layout === "container-fluid" ? layout : "container"}>
            <div className="row">
              <div className="col-xl-2 col-lg-2 col-md-6 col-4 align-self-center">
                <Logo
                  imageUrl="/assets/img/logo/logo-liggend.webp"
                  logoClass="logo"
                />
              </div>
              <div className="col-xl-7 col-lg-7 d-none d-lg-block">
                <NavMenu />
              </div>
              <div className="col-xl-1 col-lg-1 d-lg-flex align-self-center justify-content-end">
                <SiteWideSearch />
              </div>
              <div className="col-xl-2 col-lg-2 col-md-6 col-8 align-self-center">
                <IconGroup />
              </div>
            </div>
          </div>
        </DesktopHeaderContent>
      </DesktopHeader>
      <MobileHeader>
        <div
          ref={stickyRef}
          className={"sticky-bar stick header-res-padding clearfix bg-shadow"}
          style={{ top: 0 }}
        >
          <MobileItemsWrapper>
            <div className={"items-container"}>
              <HamburgerMenu
                onClick={() => {
                  setNavMenuOpen(!navMenuOpen);
                  setAccountDrawerOpen(false);
                }}
                className={navMenuOpen ? "active" : ""}
              >
                <span />
                <span />
                <span />
              </HamburgerMenu>
              {/* account icon */}
              {authUser && (
                <AccountButton
                  onClick={() => {
                    closeMenu();
                    setAccountDrawerOpen(!accountDrawerOpen);
                  }}
                >
                  <AccountIcon />
                </AccountButton>
              )}
            </div>
            {/* header logo */}
            <Logo
              imageUrl="/assets/img/logo/logo-liggend.webp"
              logoClass="logo"
              closeMenu={closeMenu}
            />
            <div className={"items-container right"}>
              <SiteWideSearch />
              <CartLink className="icon-cart" to={urls.cart}>
                <CartIcon />
                <Counter>
                  <span>{cartItemsAmount}</span>
                </Counter>
              </CartLink>
            </div>
          </MobileItemsWrapper>
          {/* mobile menu */}
          <MobileNavDrawer
            active={navMenuOpen}
            closeMenu={closeMenu}
            openAccountDrawer={() => setAccountDrawerOpen(true)}
          />
          {authUser && (
            <MobileAccountDrawer
              active={accountDrawerOpen}
              closeDrawer={() => setAccountDrawerOpen(false)}
            />
          )}
        </div>
        <StickySpacer
          style={{ height: stickyHeight || DEFAULT_STICKY_HEIGHT_MOBILE }}
        />
        <UspHolder>
          <UspBar />
        </UspHolder>
        {discountBannerActive && (
          <DiscountBanner data={discountBannerData} mobileOnly />
        )}
      </MobileHeader>
    </header>
  );
};

const HamburgerMenu = styled.div`
  display: block;
  position: relative;
  z-index: 1;

  -webkit-user-select: none;
  user-select: none;
  align-self: end;
  padding-bottom: 4px;

  span {
    display: block;
    width: 28px;
    height: 3px;
    margin-bottom: 5px;
    position: relative;

    background: ${colors.hamburger};
    border-radius: 3px;

    z-index: 1;

    transform-origin: 4px 0px;

    transition:
      transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1),
      background 0.5s cubic-bezier(0.77, 0.2, 0.05, 1),
      opacity 0.55s ease;
  }

  span:first-child {
    transform-origin: 0% 0%;
  }

  span:nth-last-child(2) {
    transform-origin: 0% 100%;
  }

  &.active {
    span {
      opacity: 1;
      transform: rotate(45deg) translate(1px, -3px);
      background: ${colors.hamburger};
    }

    span:nth-last-child(2) {
      opacity: 0;
      transform: rotate(0deg) scale(0.2, 0.2);
    }

    span:nth-last-child(1) {
      transform: rotate(-45deg) translate(0, -1px);
    }
  }
`;

const AccountButton = styled.button`
  background: none;
  border: none;
`;

const AccountIcon = styled.div`
  width: 28px;
  height: 28px;
  background-color: #333;
  -webkit-mask: url("/assets/img/button-icons/account.svg") no-repeat center;
  mask: url("/assets/img/button-icons/account.svg") no-repeat center / contain;
  transition: background-color 0.2s ease;
`;

const MobileItemsWrapper = styled.div`
  display: grid;
  padding-left: 1rem;
  padding-right: 1rem;
  grid-template-columns: 1fr 125px 1fr;

  .items-container {
    display: flex;
    gap: 6px;
  }

  .right {
    justify-content: flex-end;
  }
`;

const DesktopHeaderContent = styled.div`
  position: relative;
`;

const CartLink = styled(Link)`
  width: fit-content;
  font-size: 36px;
  position: relative;
  display: inline-block;
  border: none;
  background: transparent;
  align-self: end;
  justify-self: end;
  padding-bottom: 4px;
`;

const Counter = styled.div`
  display: flex;
  font-size: 12px;
  line-height: 21px;
  position: absolute;
  top: -12px;
  right: -9px;
  width: 20px;
  height: 20px;
  justify-content: center;
  color: #fff;
  border-radius: 100%;
  background-color: ${colors.primary};

  span {
    align-self: center;
  }
`;

const CartIcon = styled.div`
  width: 25px;
  height: 25px;
  background-color: #333;
  -webkit-mask: url("/assets/img/button-icons/cart.svg") no-repeat center;
  mask: url("/assets/img/button-icons/cart.svg") no-repeat center / contain;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.primary};
  }

  @media ${device.lg} {
    width: 32px;
    height: 32px;
  }
`;

const StickySpacer = styled.div`
  width: 100%;
  height: 0; /* Height is controlled inline based on measured sticky height */
`;

const UspHolder = styled.div`
  /* Wrapper to push USP bar below the fixed sticky header on mobile */
  width: 100%;
`;

const MobileHeader = styled.div`
  display: block;
  @media ${device.lg} {
    display: none;
  }
`;

const DesktopHeader = styled.div`
  display: none;
  @media ${device.lg} {
    display: block;
  }
`;

Header.propTypes = {
  borderStyle: PropTypes.string,
  headerPaddingClass: PropTypes.string,
  headerPositionClass: PropTypes.string,
  layout: PropTypes.string,
  top: PropTypes.string,
  onHeightChange: PropTypes.func,
};

export default Header;
